import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Plus, Target } from 'lucide-react'
import { toast } from 'sonner'
import { listGoals, pauseGoal, reactivateGoal, resumeGoal, type GoalResponse } from '@/api/goals'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { sortNeedsAttentionFirst } from '@/lib/goalPace'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { cn } from '@/lib/utils'
import { PANEL } from '@/components/budget/MonthlyBudgets'
import {
  CompleteGoalConfirm,
  ContributeModal,
  DeleteGoalConfirm,
  GoalFormSheet,
  WithdrawModal,
} from '@/components/goals/GoalModals'
import { GoalRow, type GoalMenuItem } from '@/components/goals/GoalRow'
import { GoalsHero } from '@/components/goals/GoalsHero'
import { WhereItSits } from '@/components/goals/WhereItSits'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { TabButton } from '@/components/ui/tab-button'

type Tab = 'active' | 'paused' | 'done'

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; goal: GoalResponse }
  | { type: 'contribute'; goal: GoalResponse }
  | { type: 'withdraw'; goal: GoalResponse }
  | { type: 'delete'; goal: GoalResponse }
  | { type: 'complete'; goal: GoalResponse }

function sum(values: number[]): number {
  return fromPaise(values.reduce((total, v) => total + toPaise(v), 0))
}

function GoalPanel({
  title,
  aside,
  children,
}: {
  title: string
  aside?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section aria-label={title} className={PANEL}>
      <div className="flex items-baseline justify-between gap-3 pb-1 pt-3.5">
        <h2 className="text-[15px] font-semibold text-[var(--fl-text-1)]">{title}</h2>
        {aside && <span className="text-xs text-[var(--fl-text-2)]">{aside}</span>}
      </div>
      {children}
    </section>
  )
}

export default function GoalsPage() {
  const isReadOnly = useIsReadOnly()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const { formatWhole } = useFormatCurrency()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const param = searchParams.get('tab')
  const tab: Tab = param === 'paused' || param === 'done' ? param : 'active'
  const [modal, setModal] = useState<ModalState>({ type: 'none' })
  const close = () => setModal({ type: 'none' })
  const today = new Date()

  const { data, isLoading, isError } = useQuery({ queryKey: ['goals'], queryFn: () => listGoals() })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['goals'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }
  const pauseMutation = useMutation({
    mutationFn: (id: string) => pauseGoal(id),
    onSuccess: refresh,
    onError: () => toast.error('Failed to pause goal. Please try again.'),
  })
  const resumeMutation = useMutation({
    mutationFn: (id: string) => resumeGoal(id),
    onSuccess: refresh,
    onError: () => toast.error('Failed to resume goal. Please try again.'),
  })
  const reactivateMutation = useMutation({
    mutationFn: (id: string) => reactivateGoal(id),
    onSuccess: refresh,
    onError: () => toast.error('Failed to reactivate goal. Please try again.'),
  })

  function menuFor(goal: GoalResponse): GoalMenuItem[] {
    if (isReadOnly) return []
    const edit = { label: 'Edit', onSelect: () => setModal({ type: 'edit', goal }) }
    const remove = {
      label: 'Delete',
      danger: true,
      onSelect: () => setModal({ type: 'delete', goal }),
    }
    if (goal.status === 'COMPLETED') {
      return [{ label: 'Reactivate', onSelect: () => reactivateMutation.mutate(goal.id) }, remove]
    }
    return [
      { label: 'Add money', onSelect: () => setModal({ type: 'contribute', goal }) },
      { label: 'Withdraw', onSelect: () => setModal({ type: 'withdraw', goal }) },
      edit,
      goal.status === 'ACTIVE'
        ? { label: 'Pause', onSelect: () => pauseMutation.mutate(goal.id) }
        : { label: 'Resume', onSelect: () => resumeMutation.mutate(goal.id) },
      { label: 'Mark done', onSelect: () => setModal({ type: 'complete', goal }) },
      remove,
    ]
  }

  function row(goal: GoalResponse) {
    const canMove = !isReadOnly && goal.status !== 'COMPLETED'
    return (
      <GoalRow
        key={goal.id}
        goal={goal}
        today={today}
        menu={menuFor(goal)}
        onAdd={canMove ? () => setModal({ type: 'contribute', goal }) : undefined}
        onWithdraw={canMove ? () => setModal({ type: 'withdraw', goal }) : undefined}
      />
    )
  }

  function selectTab(next: Tab) {
    setSearchParams(next === 'active' ? {} : { tab: next }, { replace: true })
  }

  const active = data ? sortNeedsAttentionFirst(data.active) : []
  const paused = data?.paused ?? []
  const done = data?.completed ?? []
  const noGoals = !!data && active.length + paused.length + done.length === 0

  const saved = sum(active.map((g) => g.currentAmount))
  const target = sum(active.map((g) => g.targetAmount))
  const percent = target > 0 ? Math.round((saved / target) * 100) : 0
  // Optional fields: an older API leaves them out, and the hero then drops those figures.
  const hasPace = active.some((g) => g.addedThisMonth != null)
  const addedThisMonth = sum(active.map((g) => g.addedThisMonth ?? 0))
  const dated = active.filter((g) => g.neededPerMonth != null)
  const needed = sum(dated.map((g) => g.neededPerMonth ?? 0))

  return (
    <div className="flex flex-col gap-3 p-4 md:gap-5 md:p-10">
      <header className="flex items-center justify-between gap-3">
        <h2 className="font-serif text-xl font-semibold text-[var(--fl-heading)] md:text-[40px] md:font-bold md:leading-tight">
          Goals
        </h2>
        {!isReadOnly && (
          <Button
            aria-label="New goal"
            variant={wide ? 'default' : 'outline'}
            onClick={() => setModal({ type: 'create' })}
            className={cn(
              'font-semibold active:scale-95',
              wide
                ? 'h-auto px-4 py-2.5'
                : 'h-9 gap-1 rounded-full border-[var(--fl-primary)] px-3 text-[13px] text-[var(--fl-primary)]',
              noGoals && 'motion-safe:animate-attention-pulse'
            )}
          >
            <Plus size={wide ? 16 : 14} /> {wide ? 'New goal' : 'New'}
          </Button>
        )}
      </header>

      {isLoading && <p className="text-sm text-[var(--fl-text-2)]">Loading…</p>}
      {isError && <p className="text-sm text-[var(--fl-error)]">Failed to load goals.</p>}

      {noGoals && (
        <EmptyState
          icon={Target}
          title="Name your first savings goal"
          description="Set a target — an emergency fund, a trip, a down payment — and watch every contribution move you toward it."
        />
      )}

      {data && !noGoals && (
        <>
          <div
            role="tablist"
            aria-label="Goal status"
            className="flex gap-1 border-b border-[var(--fl-border)]"
          >
            <TabButton
              active={tab === 'active'}
              count={active.length}
              onClick={() => selectTab('active')}
            >
              Active
            </TabButton>
            <TabButton
              active={tab === 'paused'}
              count={paused.length}
              onClick={() => selectTab('paused')}
            >
              Paused
            </TabButton>
            <TabButton
              active={tab === 'done'}
              count={done.length}
              onClick={() => selectTab('done')}
            >
              Done
            </TabButton>
          </div>

          {tab === 'active' && (
            <>
              {active.length > 0 && (
                <GoalsHero
                  ariaLabel="Goals summary"
                  eyebrow={`${active.length} active ${active.length === 1 ? 'goal' : 'goals'}`}
                  label="Saved so far"
                  amount={saved}
                  note={`of ${formatWhole(target)} across all goals · ${percent}%`}
                  percent={percent}
                  items={[
                    ...(hasPace
                      ? [
                          {
                            label: wide ? 'Added this month' : 'This month',
                            value: `${addedThisMonth < 0 ? '−' : '+'}${formatWhole(Math.abs(addedThisMonth))}`,
                            tone: 'income' as const,
                          },
                        ]
                      : []),
                    ...(dated.length > 0
                      ? [
                          {
                            label: wide ? 'Needed each month' : 'Needed',
                            value: formatWhole(needed),
                            sub: wide ? 'to hit every date' : '/mo',
                          },
                        ]
                      : []),
                  ]}
                />
              )}
              <div className="grid grid-cols-1 items-start gap-3 md:gap-[18px] lg:grid-cols-[1.55fr_1fr]">
                <GoalPanel
                  title="Active goals"
                  aside={wide ? 'Needs attention first · ▏ = time gone' : undefined}
                >
                  {active.length === 0 ? (
                    <p className="py-3 text-sm text-[var(--fl-text-2)]">No active goals.</p>
                  ) : (
                    <ul className="divide-y divide-[var(--fl-divider)]">{active.map(row)}</ul>
                  )}
                </GoalPanel>
                <div className="flex flex-col gap-3 md:gap-[18px]">
                  <WhereItSits accounts={data.earmarkAccounts ?? []} activeGoals={active} />
                  {wide && paused.length + done.length > 0 && (
                    <GoalPanel title="Paused & done">
                      <ul className="divide-y divide-[var(--fl-divider)]">
                        {[...paused, ...done].slice(0, 4).map((g) => (
                          <GoalRow key={g.id} goal={g} today={today} menu={[]} />
                        ))}
                      </ul>
                      <button
                        type="button"
                        onClick={() => selectTab(paused.length > 0 ? 'paused' : 'done')}
                        className="pb-2 text-xs font-semibold text-[var(--fl-primary)] hover:underline"
                      >
                        See all
                      </button>
                    </GoalPanel>
                  )}
                </div>
              </div>
            </>
          )}

          {tab !== 'active' && (
            <GoalPanel title={tab === 'paused' ? 'Paused goals' : 'Done'}>
              {(tab === 'paused' ? paused : done).length === 0 ? (
                <p className="py-3 text-sm text-[var(--fl-text-2)]">
                  {tab === 'paused' ? 'No paused goals.' : 'No goals done yet.'}
                </p>
              ) : (
                <ul className="divide-y divide-[var(--fl-divider)]">
                  {(tab === 'paused' ? paused : done).map(row)}
                </ul>
              )}
            </GoalPanel>
          )}
        </>
      )}

      {(modal.type === 'create' || modal.type === 'edit') && (
        <GoalFormSheet goal={modal.type === 'edit' ? modal.goal : null} onClose={close} />
      )}
      {modal.type === 'contribute' && <ContributeModal goal={modal.goal} onClose={close} />}
      {modal.type === 'withdraw' && <WithdrawModal goal={modal.goal} onClose={close} />}
      {modal.type === 'delete' && <DeleteGoalConfirm goal={modal.goal} onClose={close} />}
      {modal.type === 'complete' && <CompleteGoalConfirm goal={modal.goal} onClose={close} />}
    </div>
  )
}
